import React from "react";
import styled from "styled-components";
import { HourglassIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";

/**
 * Where a sparkline goes on a smartfolio that has no valuation yet.
 *
 * The glyph was a Microsoft Fluent icon pasted in from Sketch: a 30px SVG
 * carrying `fill="red"` on its root, a `<title>` reading
 * "ic_fluent_shifts_pending_24_regular", two hardcoded greys, and layer
 * names with emoji in them. It drew a calendar with a question mark clipped
 * into its corner, which at 30px in a card read as a broken image rather
 * than as a state, and it belonged to no icon family this product uses.
 *
 * An hourglass instead, from the product's own set, in the grey the caption
 * beside it wears: the smartfolio is not unvalued, it is not valued YET, and
 * the difference is the whole message. It stands in the slot the line would
 * occupy, so the row keeps its shape whether a smartfolio has a history or
 * is waiting for its first one.
 *
 * The caption travels with the glyph. One of the two places this state
 * appears used to show the icon alone, which asked a reader to guess.
 */
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: ${colors.gray[400]};
`;

const Caption = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
  color: ${colors.gray[500]};
`;

type Props = {
  /** Left off where the layout already labels the slot. */
  caption?: string;
  className?: string;
};

const NotYetValued: React.FC<Props> = ({
  caption = "Not yet valued",
  className,
}) => (
  <Wrap className={className}>
    {/* A shade lighter than the caption, so the words lead and the mark
        supports them. */}
    <HugeIcon icon={HourglassIcon} size={22} strokeWidth={1.5} />
    {caption ? <Caption>{caption}</Caption> : null}
  </Wrap>
);

export default NotYetValued;
